import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import { PageConsultation } from "@components/layouts/PageConsultation";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button, Feedback, FormGroup, Label, TextField
} from "@runwayhealth/runway-components-react";
import { convertBiometrics } from "@utils/questionnaire/biometrics";

interface IFormInputs {
  feet: number | null;
  inches: number | null;
  weight: number | null;
}

const schema = yup
  .object()
  .shape({
    feet: yup.number().positive().max(10).typeError("Feets must be a number").required("Required"),
    inches: yup
      .number()
      .positive()
      .max(11)
      .typeError("Inches must be a number")
      .required("Required"),
    weight: yup
      .number()
      .positive()
      .max(2000)
      .typeError("Weight must be a number")
      .required("Required"),
  })
  .required();

const defaultValues: IFormInputs = {
  feet: null,
  inches: null,
  weight: null,
};

export interface Biometrics {
  height: number;
  weight: number;
}

type BiometricsPageProps = {
  onAnswer: (answer: Biometrics) => void;
};

const BiometricsPage: React.FC<BiometricsPageProps> = ({ onAnswer }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<IFormInputs>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues,
  });
  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    onAnswer(convertBiometrics(data.feet, data.inches, data.weight));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageConsultation.Body>
        <FormGroup>
          <Row>
            <Col md={2}></Col>
            <Col md={2} style={{ textAlign: "left" }}>
              <Label htmlFor="feet">Feet</Label>
              <TextField
                id="feet"
                placeholder="5"
                isInvalid={errors.feet}
                type="number"
                {...register("feet")}
              />
              <Feedback size="sm" isInvalid>
                {errors.feet?.message}
              </Feedback>
            </Col>
            <Col md={2} style={{ textAlign: "left" }}>
              <Label htmlFor="inches">Inches</Label>
              <TextField
                id="inches"
                placeholder="6"
                isInvalid={errors.inches}
                type="number"
                inputMode="numeric"
                {...register("inches")}
              />
              <Feedback size="sm" isInvalid>
                {errors.inches?.message}
              </Feedback>
            </Col>
            <Col md={4} style={{ textAlign: "left" }}>
              <Label htmlFor="weight">Weight</Label>
              <TextField
                id="weight"
                placeholder="145"
                isInvalid={errors.weight}
                type="number"
                inputMode="numeric"
                {...register("weight")}
              />
              <Feedback size="sm" isInvalid>
                {errors.weight?.message}
              </Feedback>
            </Col>
          </Row>
        </FormGroup>
      </PageConsultation.Body>
      <Button size="lg" iconName="ArrowRight" type="submit" disabled={!isDirty || !isValid}>
        Continue
      </Button>
    </form>
  );
};

export default BiometricsPage;
